import { useState, useCallback, useEffect, useRef } from 'react';
import type { Product, BasketProduct } from '../types/api';
import type { CartItem } from '../types/app';
import { apiClient } from '../services/api';
import { useToast } from './useToast';

interface UseCartOptions {
  callId: number;
  auth: { accessToken: string; serviceId: string | number };
  enabled?: boolean; // Defaults to true; demo mode is intercepted at the API layer
}

interface UseCartReturn {
  items: CartItem[];
  isOpen: boolean;
  isLoading: boolean;
  error: string | null;
  total: string;
  addItem: (product: Product, identifier: { variantId?: number; remoteObjId?: string }, quantity?: number, selectedProductOptions?: Record<string, string>) => Promise<void>;
  addComboItems: (items: Array<{ product: Product; variantId: number; discountVal: number }>) => Promise<void>;
  removeItem: (basketProductId: number) => Promise<void>;
  removeByProductId: (productId: number) => Promise<void>;
  updateQuantity: (basketProductId: number, variantId: number, quantity: number) => Promise<void>;
  // Composite (product_id, variant_id) lookup — required so PDP can reflect per-variant
  // "Reserved/Reserve" state without false positives on sibling variants of the same product.
  findInCart: (productId: number, variantId: number) => CartItem | undefined;
  clearCart: () => void;
  toggleCart: () => void;
  refetch: () => Promise<void>;
  totalItems: number;
}

/**
 * Map BasketProduct from API response to CartItem for local state.
 *
 * Out-of-scope items (server qty=0) display as 1 — server-side total stays $0
 * until the user clicks "+", at which point a real PATCH lifts the row out of
 * the qty=0 state. Display-only normalization; no auto-bump to the server.
 */
function mapBasketProductToCartItem(bp: BasketProduct): CartItem {
  return {
    id: bp.id,
    product: bp.product,
    productVariant: bp.product_variant,
    quantity: bp.quantity === 0 ? 1 : bp.quantity,
    discountVal: bp.discount_val,
  };
}

export function useCart(options?: UseCartOptions): UseCartReturn {
  const [items, setItems] = useState<CartItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState<string>('0.00');
  const { addToast } = useToast();

  const enabled = options?.enabled ?? true;
  const callId = options?.callId;
  const auth = options?.auth;

  // Track mount state for stale response prevention
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  /**
   * Apply BasketResponse data to local state.
   */
  const applyResponse = useCallback((products: BasketProduct[], responseTotal: string) => {
    if (!mountedRef.current) return;
    setItems(products.map(mapBasketProductToCartItem));
    setTotal(responseTotal);
    setError(null);
  }, []);

  /**
   * Fetch basket from API and update local state.
   */
  const refetch = useCallback(async () => {
    if (!callId || !auth) return;
    try {
      // Show loader only on the initial fetch (empty cart). Subsequent refetches
      // — notably the WebSocket basket_updated round-trip fired after an optimistic
      // updateQuantity — must stay silent so CartOverlay does not swap its list for
      // <SkeletonItem /> between the optimistic update and the server-confirmed state.
      // SPEC-064 AC-1/AC-2 (no-flicker on qty changes).
      if (items.length === 0) setIsLoading(true);
      const response = await apiClient.getBasketProducts(callId, auth);
      applyResponse(response.products, response.total);
    } catch (err) {
      if (mountedRef.current) {
        const msg = err instanceof Error ? err.message : 'Failed to load basket';
        setError(msg);
      }
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, [callId, auth, applyResponse, items.length]);

  // Fetch basket on mount when enabled
  useEffect(() => {
    if (enabled && callId && auth) {
      refetch();
    }
    // Only run on mount or when enabled/callId change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, callId]);

  /**
   * Add item to basket via API with optimistic update.
   */
  const addItem = useCallback(async (product: Product, identifier: { variantId?: number; remoteObjId?: string }, quantity: number = 1, selectedProductOptions?: Record<string, string>) => {
    if (!callId || !auth) return;

    // Optimistic: add item locally
    const prevItems = items;
    const prevTotal = total;
    const existing = items.find((item) => item.product.id === product.id);
    if (existing) {
      setItems((prev) =>
        prev.map((item) =>
          item.product.id === product.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        )
      );
    } else {
      setItems((prev) => [
        ...prev,
        {
          id: -Date.now(), // Temp ID until server responds
          product,
          productVariant: { id: identifier.variantId ?? -1, title: product.title, price: product.price },
          quantity,
          discountVal: 0,
        },
      ]);
    }

    try {
      const response = await apiClient.addBasketProduct(callId, identifier, auth, { quantity, selectedProductOptions });
      applyResponse(response.products, response.total);
    } catch (err) {
      // Revert optimistic update
      if (mountedRef.current) {
        setItems(prevItems);
        setTotal(prevTotal);
        const msg = err instanceof Error ? err.message : 'Failed to add item';
        setError(msg);
        addToast({ type: 'error', title: 'Failed to add item', message: msg });
      }
    }
  }, [callId, auth, items, total, applyResponse, addToast]);

  /**
   * Remove item from basket by basketProductId.
   */
  const removeItem = useCallback(async (basketProductId: number) => {
    if (!callId || !auth) return;

    // Optimistic: remove locally
    const prevItems = items;
    const prevTotal = total;
    setItems((prev) => prev.filter((item) => item.id !== basketProductId));

    try {
      const response = await apiClient.removeBasketProduct(callId, basketProductId, auth);
      applyResponse(response.products, response.total);
    } catch (err) {
      // Revert optimistic update
      if (mountedRef.current) {
        setItems(prevItems);
        setTotal(prevTotal);
        const msg = err instanceof Error ? err.message : 'Failed to remove item';
        setError(msg);
        addToast({ type: 'error', title: 'Failed to remove item', message: msg });
      }
    }
  }, [callId, auth, items, total, applyResponse, addToast]);

  /**
   * Remove item by product ID (finds basket item internally).
   * Used by ProductsPanel's un-reserve flow where only product.id is known.
   */
  const removeByProductId = useCallback(async (productId: number) => {
    const item = items.find((i) => i.product.id === productId);
    if (!item) return; // No-op if not found
    await removeItem(item.id);
  }, [items, removeItem]);

  /**
   * Update item quantity. If quantity <= 0, removes the item.
   */
  const updateQuantity = useCallback(async (basketProductId: number, variantId: number, quantity: number) => {
    if (!callId || !auth) return;

    if (quantity <= 0) {
      await removeItem(basketProductId);
      return;
    }

    // Optimistic: update locally
    const prevItems = items;
    const prevTotal = total;
    setItems((prev) =>
      prev.map((item) =>
        item.id === basketProductId ? { ...item, quantity } : item
      )
    );

    try {
      const response = await apiClient.addBasketProduct(callId, { variantId }, auth, { quantity });
      applyResponse(response.products, response.total);
    } catch (err) {
      // Revert optimistic update
      if (mountedRef.current) {
        setItems(prevItems);
        setTotal(prevTotal);
        const msg = err instanceof Error ? err.message : 'Failed to update quantity';
        setError(msg);
        addToast({ type: 'error', title: 'Failed to update quantity', message: msg });
      }
    }
  }, [callId, auth, items, total, removeItem, applyResponse, addToast]);

  /**
   * Add multiple combo items sequentially to the basket with individual discounts.
   * Sequential (not parallel) to avoid race conditions with basket state (Assumption A-6).
   * Each item is added with quantity: 1 and the provided discountVal (Assumption A-7).
   * After all items added (or on failure), refetch once to sync local state.
   * The main combo parent product is NOT added — only child product variants (AC-19).
   */
  const addComboItems = useCallback(async (comboItems: Array<{ product: Product; variantId: number; discountVal: number }>) => {
    if (!callId || !auth) return;
    if (comboItems.length === 0) return;

    try {
      // Execute sequentially to avoid race conditions (AC-16, AC-17, AC-18)
      for (const item of comboItems) {
        await apiClient.addBasketProduct(callId, { variantId: item.variantId }, auth, {
          quantity: 1,        // AC-18: always quantity 1 per combo item
          discountVal: item.discountVal, // AC-17: discount_val from parent's combo_product_discount
        });
      }
    } catch (err) {
      // On any failure, show error toast but still refetch to get accurate state
      if (mountedRef.current) {
        const msg = err instanceof Error ? err.message : 'Failed to add combo items';
        setError(msg);
        addToast({ type: 'error', title: 'Failed to add combo items', message: msg });
      }
    } finally {
      // Always refetch once after all items are processed (success or partial failure)
      await refetch();
    }
  }, [callId, auth, refetch, addToast]);

  /**
   * Composite-key lookup: returns the cart item matching both product id and variant id.
   * Callers (PDP) need this because product-id-only matching produces false positives on
   * sibling variants of the same product when only one variant is reserved.
   */
  const findInCart = useCallback(
    (productId: number, variantId: number): CartItem | undefined =>
      items.find((item) => item.product.id === productId && item.productVariant.id === variantId),
    [items]
  );

  const clearCart = useCallback(() => {
    setItems([]);
    setTotal('0.00');
  }, []);

  const toggleCart = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);

  return {
    items,
    isOpen,
    isLoading,
    error,
    total,
    addItem,
    addComboItems,
    removeItem,
    removeByProductId,
    updateQuantity,
    findInCart,
    clearCart,
    toggleCart,
    refetch,
    totalItems,
  };
}
